<template>
    <div>
        <v-select
            v-model="selectedItem"
            :options="optionsValidated"
            :close-on-select="true"
            label="title"
            class="mw-100"
            :placeholder="placeholder"
            :get-option-key="(i) => i.id"
            :disabled="disabled"
            @search="search"
            @focus="handleFocus()"
            ref="folderSelector"
        >
            <template slot="option" slot-scope="option">
                <div class="d-flex align-items-center py-1">
                    <div v-if="['owned_case', 'community_reference'].includes(option.item_type)" class="flex-grow-0">
                        <div v-if="option.case.cover_image">
                            <img :src="option.case.cover_image.signed_url" class="rounded selected-case-image" />
                        </div>
                        <div
                            v-else
                            class="d-flex align-items-center justify-content-center selected-case-image bg-offwhite rounded"
                        >
                            <span class="icon icon-lg icon-glyphicons-basic-picture text-light"></span>
                        </div>
                    </div>
                    <div v-else class="flex-grow-0">
                        <span class="icon icon-glyphicons-basic-folder text-secondary" style="font-size: 34px"></span>
                    </div>
                    <div class="flex-grow-1 pl-2">
                        <div class="small mb-1">
                            {{ option.title }}
                        </div>
                        <div class="d-flex align-items-center" v-if="option.id !== null">
                            <span class="badge badge-light case-badge align-middle"> Library </span>
                            <div
                                v-for="(folderName, index) in option.parent_folder_name_list"
                                :key="index"
                                class="d-flex align-items-center"
                            >
                                <span
                                    class="icon icon-xs icon-glyphicons-basic-chevron-right text-secondary mx-1 align-middle"
                                ></span>
                                <span class="badge badge-light case-badge align-middle">
                                    {{ trimString(folderName, 20) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:selected-option="option">
                <div class="d-flex align-items-center py-1">
                    <div v-if="['owned_case', 'community_reference'].includes(option.item_type)" class="flex-grow-0">
                        <div v-if="option.case.cover_image">
                            <img :src="option.case.cover_image.signed_url" class="rounded selected-case-image" />
                        </div>
                        <div
                            v-else
                            class="d-flex align-items-center justify-content-center selected-case-image bg-offwhite rounded"
                        >
                            <span class="icon icon-lg icon-glyphicons-basic-picture text-light"></span>
                        </div>
                    </div>
                    <div v-else class="flex-grow-0">
                        <span class="icon icon-glyphicons-basic-folder text-secondary" style="font-size: 34px"></span>
                    </div>
                    <div class="flex-grow-1 pl-2 ml-1">
                        <div class="medium mb-1">
                            {{ option.title }}
                        </div>
                    </div>
                </div>
            </template>
            <template slot="no-options">
                <div class="py-2">
                    <span class="text-secondary">{{ emptyText }}</span>
                </div>
            </template>
            <template v-slot:spinner="{ loading }">
                <div v-show="loading">
                    <div class="d-flex align-items-center">
                        <div class="spinner-border spinner-border-sm text-primary mr-2" role="status">
                            <span class="sr-only">Loading...</span>
                        </div>
                    </div>
                </div>
            </template>
        </v-select>
    </div>
</template>

<script>
import http from "@/axios.js"
import debounce from "lodash.debounce"
import Utilities from "@/assets/js/utilities.js"

export default {
    name: "LibraryItemSelector",
    props: {
        value: {
            type: Object,
            required: false,
        },
        placeholder: {
            type: String,
            required: false,
            default: "Search folders and scenarios...",
        },
        emptyText: {
            type: String,
            required: false,
            default: "No items found.",
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        pageId: {
            type: Number,
            required: false,
            default: null,
        },
        itemTypes: {
            type: Array,
            required: false,
        },
    },
    data() {
        return {
            options: [],
            selectedItem: this.value,
            searchQuery: "",
        }
    },
    computed: {
        optionsValidated() {
            let optionsValidated = this.options.slice()
            if (this.itemType === "folder") {
                // Add a 'null' ID option representing the root folder (i.e. no parent ID).
                optionsValidated.push({
                    id: null,
                    title: "Library",
                })
            }
            if (this.value) {
                // Remove the currently selected item from the list.
                let selectedItemIndex = optionsValidated.findIndex((item) => item.id === this.value.id)
                if (selectedItemIndex !== -1) {
                    optionsValidated.splice(selectedItemIndex, 1)
                }
            }
            return optionsValidated
        },
    },
    watch: {
        selectedItem: function (newValue) {
            this.$emit("input", newValue)
        },
    },
    methods: {
        // A callback used to search for folder-type library items.
        search(search, loading) {
            this.searchQuery = search
            loading(true)
            this.searchDebounced(search, loading)
        },
        // Debounced function to search for folder-type library items.
        searchDebounced: debounce(async function (search, loading = null) {
            // Store a random search to make sure that an older search query doesn't
            // overwrite new search results.
            let searchId = Utilities.getRandomIdStr(10)
            this.searchId = searchId
            let vm = this
            let queryParams = {
                query: search,
                organizationId: this.organizationId,
            }
            if (this.pageId) {
                queryParams["pageId"] = this.pageId
            }
            if (this.itemTypes) {
                queryParams.itemTypes = this.itemTypes.join(",")
            }
            http.get("/api/library-items/search/", {
                params: queryParams,
            })
                .then(function (response) {
                    if (searchId === vm.searchId) {
                        vm.options = response.data
                    }
                })
                .finally(() => {
                    if (loading) {
                        loading(false)
                    }
                })
        }, 150),
        // Trim a string to the desired length, for display.
        trimString(str, length) {
            return Utilities.trimString(str, length)
        },
        // Handle the selector being brought into focus.
        handleFocus() {
            this.searchDebounced(this.searchQuery)
        },
        // Force an option to be selected, even if it doesn't exist in the list of
        // currently loaded options.
        forceSelectItem(itemObject) {
            this.selectedItem = itemObject
        },
    },
}
</script>

<style lang="scss" scoped>
.selected-case-image {
    object-fit: cover;
    height: 40px;
    width: 60px;
}
</style>